import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaBell,
  FaBriefcase,
  FaUserPlus,
  FaCheckCircle,
} from "react-icons/fa";
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "../../Helper/notificationsApi";
import { isAuthenticated, subscribeToPushNotifications } from "../../Helper/pwa";
import toast from "react-hot-toast";
import "./NotificationBell.css";

const getNotifIcon = (icon) => {
  if (icon === "briefcase") return <FaBriefcase />;
  if (icon === "user") return <FaUserPlus />;
  if (icon === "check") return <FaCheckCircle />;
  return <FaBell />;
};

/**
 * @param {"admin"|"modern"} variant
 */
const NotificationBell = ({ variant = "modern" }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const wrapRef = useRef(null);

  const load = useCallback(async () => {
    if (!isAuthenticated()) return;
    try {
      setLoading(true);
      const data = await fetchNotifications(30);
      if (data?.success) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error("Failed to load notifications", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated()) return undefined;
    load();
    if (typeof Notification !== "undefined" && Notification.permission === "granted") {
      subscribeToPushNotifications();
    }
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const toggleOpen = async () => {
    const next = !open;
    setOpen(next);
    if (next) await load();
  };

  const onEnableAlerts = async () => {
    localStorage.setItem("push_prompted", "1");
    const result = await subscribeToPushNotifications();
    if (result.success) {
      toast.success("Job alerts enabled on this device");
    } else if (result.reason === "permission_denied") {
      toast.error("Allow notifications in Android settings for CAJobPortal");
    } else {
      toast.error("Could not enable alerts. Try again from the app.");
    }
  };

  const onMarkAll = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error(error);
    }
  };

  const onItemClick = async (id) => {
    try {
      await markNotificationRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
      setUnreadCount((count) => Math.max(0, count - 1));
    } catch (error) {
      console.error(error);
    }
    setOpen(false);
  };

  const rootClass =
    variant === "admin" ? "notif-bell notif-bell--admin" : "notif-bell notif-bell--modern";

  return (
    <div className={rootClass} ref={wrapRef}>
      <button
        type="button"
        className="notif-bell__btn"
        onClick={toggleOpen}
        aria-expanded={open}
        aria-label="Notifications"
      >
        <FaBell />
        {unreadCount > 0 && (
          <span className="notif-bell__badge">{unreadCount > 9 ? "9+" : unreadCount}</span>
        )}
      </button>

      {open && (
        <div className="notif-bell__panel" role="menu">
          <div className="notif-bell__header">
            <span>Notifications</span>
            {unreadCount > 0 && (
              <button type="button" onClick={onMarkAll}>
                Mark all read
              </button>
            )}
          </div>
          <div className="notif-bell__list">
            {loading && notifications.length === 0 && (
              <div className="notif-bell__empty">Loading…</div>
            )}
            {!loading && notifications.length === 0 && (
              <div className="notif-bell__empty">
                <p>You&apos;re all caught up. Job updates will appear here.</p>
              </div>
            )}
            {notifications.map((n) => (
              <Link
                key={n.id}
                to={n.link || "/"}
                className={`notif-bell__item${n.is_read ? "" : " is-unread"}`}
                onClick={() => onItemClick(n.id)}
              >
                <div className="notif-bell__icon">{getNotifIcon(n.icon)}</div>
                <div className="notif-bell__body">
                  <div className="notif-bell__title">{n.title}</div>
                  <div className="notif-bell__msg">{n.message}</div>
                  <div className="notif-bell__time">{n.time}</div>
                </div>
                {!n.is_read && <span className="notif-bell__dot" />}
              </Link>
            ))}
          </div>
          {typeof Notification !== "undefined" && Notification.permission !== "granted" && (
            <div className="notif-bell__footer">
              <button type="button" className="notif-bell__enable" onClick={onEnableAlerts}>
                Enable notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
